import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import {
  Badge,
  Combobox,
  FilterChip,
  ICON,
  Input,
  Select,
  Switch,
  Textarea,
  noticeInlineClass,
} from '@attendance/ui';
import { timezoneOptions } from '@attendance/shared/timezones';
import type { ReportSchedule } from '@attendance/shared/schemas/settings';
import type { SettingField } from './settingsCatalog.js';
import { DateControl, TimeControl } from './SettingsDateControls.js';
import { ReportSchedulesEditor } from './ReportSchedulesEditor.js';

/* `parseRecipients` moved out with the editor that owns it. Re-exported from
   here because `SmartReportPage` imports it from this module and validates its
   own "send to" box with the same function — one parser, one answer about what
   counts as an address. */
export { parseRecipients } from './ReportSchedulesEditor.js';

/**
 * Control rendering for the Settings page.
 *
 * Every control is driven by a `SettingField` from the catalogue, so a new
 * setting is a data change, not a new form. Four rules the whole file obeys:
 *
 *  - EVERY TYPE SIZE COMES FROM THE SCALE. The file used to be written in
 *    `text-xs`/`text-sm`/`text-base` — Tailwind's stock 12/14/16 — with a note
 *    saying 12px was the floor. That floor is still right and the spelling was
 *    wrong: the console's own steps are `text-copy` (14), `text-copy-sm` (13)
 *    and `text-label` (12.5/600), and a screen written in stock sizes cannot be
 *    re-tuned when the scale moves. `text-base` in particular had no step at all
 *    behind it. Nothing here reaches past the scale.
 *  - THE TWO CARD ROLES ARE NOT INTERCHANGEABLE. `--radius-card` (12px) means an
 *    INNER card — a block nested inside something that is already a card — and
 *    `--radius-panel` (16px) is what a top-level card, tile or table shell
 *    wants. `SectionShell` is the only top-level card in this file and is the
 *    only `rounded-panel`; the change line, the schedule row and the pay banner
 *    are all nested inside one, so all three stay `rounded-card`.
 *  - Logical properties only — `ms/me/ps/pe/start/end` — so the page mirrors
 *    cleanly under `dir="rtl"`.
 *  - Interactive rows are at least 44px tall, expressed as `min-h` plus padding
 *    and never as a fixed height, so a wrapped Arabic label grows the row.
 */

// ---------------------------------------------------------------------------
// Value formatting — used by the "current value" readouts, the before → after
// line, and the pay confirmation dialog. One implementation so all three agree.
// ---------------------------------------------------------------------------

export const WEEKDAY_KEYS = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
] as const;

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function formatSettingValue(
  field: SettingField,
  value: unknown,
  t: Translate
): string {
  switch (field.kind) {
    case 'toggle':
      return t(value === false ? 'settings.value.off' : 'settings.value.on');
    case 'weekdays': {
      const days = Array.isArray(value) ? (value as number[]) : [];
      if (days.length === 0) return t('settings.value.none');
      return days
        .map((d) => t(`settings.weekday.${WEEKDAY_KEYS[d] ?? 'sun'}`))
        .join(t('settings.listSeparator'));
    }
    case 'dayList': {
      const days = Array.isArray(value) ? (value as number[]) : [];
      if (days.length === 0) return t('settings.value.none');
      return days.join(t('settings.listSeparator'));
    }
    case 'select': {
      const match = field.options?.find((o) => o.value === value);
      return match ? t(match.labelKey) : String(value ?? '');
    }
    case 'schedules': {
      const list = Array.isArray(value) ? value : [];
      if (list.length === 0) return t('settings.value.none');
      return t('settings.value.scheduleCount', { count: list.length });
    }
    case 'number': {
      const unit = field.unitKey ? ` ${t(field.unitKey)}` : '';
      return `${String(value ?? '')}${unit}`;
    }
    case 'text':
    case 'textarea': {
      const text = typeof value === 'string' ? value.trim() : '';
      if (text === '') return t('settings.value.empty');
      return text.length > 60 ? `${text.slice(0, 60)}…` : text;
    }
    default: {
      const text = value === null || value === undefined ? '' : String(value);
      return text === '' ? t('settings.value.empty') : text;
    }
  }
}

// ---------------------------------------------------------------------------
// Badges + the before → after line
// ---------------------------------------------------------------------------

/**
 * "Affects pay" marker. Icon + words, never colour alone — the whole point is
 * that an admin skimming under time pressure cannot miss it, including one who
 * can't distinguish the amber.
 *
 * It is `Badge` now rather than a hand-rolled pill, and that is a correctness
 * change wearing a re-skin's clothes. The pill drew `text-warning` — the SOLID
 * fill's hue — on `bg-warning-surface`, the pale tint. That pairing is the exact
 * mistake the note on `.tint-warning` in apps/web/src/index.css says this app has
 * shipped three times: measured on the tint, the raw hue lands around 3.9:1 and
 * fails AA at this size, while `-on-surface` clears it with room. `Badge` encodes
 * the whole triple (`bg-warning-surface` + `border-warning-border` +
 * `text-warning-on-surface`), so the marker also gains the border that is what
 * still has an edge when a phone is read in sunlight.
 *
 * `rounded-full` went with it. A 999px pill is not a role in the shape ramp; a
 * chip is `--radius-xs` (8px), which is what `Badge` carries.
 */
export function PayTag(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant="warning">
      <AlertTriangle size={ICON.inline} aria-hidden="true" className="flex-none" />
      {t('settings.payTag')}
    </Badge>
  );
}

/**
 * "Not in force" marker, for a value the screen is showing that nothing is
 * actually computing from. Words, not colour alone — the sentence is the whole
 * signal, so this carries no icon: it sits beside `PayTag`, and a second
 * warning triangle would read as decoration, not as a second fact.
 *
 * IT IS A `Badge` NOW, and the variant is the whole question. This was a
 * hand-rolled span wearing a `bg-warning/10` wash, chosen only so it would not
 * merge into `PayTag`'s `bg-warning-surface` beside it: same chip shape, so
 * the two read as one row of markers, different fill, so they read as two
 * facts. Right goal, reached by inventing a fill — two washes of one hue is a
 * distinction nobody named, and neither has a border (§6, sunlight).
 *
 * `neutral` states the fact and is not a downgrade: `Badge` gives neutral the
 * meaning "no state to report", which is what "not in force" claims. The amber
 * belongs to `PayTag`, whose claim is the opposite, and to
 * `WorkdayEngineOffAlert` above the group, where the caution is stated at full
 * weight. Two hues for two facts reads more strongly than two strengths of one.
 *
 * The ink rule the old note recorded is the component's now: never
 * `text-*-foreground` on a tint (1.19:1 / 1.06:1 on a /10 wash — how this app
 * shipped invisible text three times, most recently in this file).
 */
export function NotInForceTag(): JSX.Element {
  const { t } = useTranslation();
  return <Badge variant="neutral">{t('settings.workdayEngine.tag')}</Badge>;
}

/**
 * The flexible net-hours + overtime engine is NOT running, and the pay group is
 * about to render nine values that look exactly like policy.
 *
 * Shown above the fields rather than beside them, because the first thing an
 * admin needs is not "this number is wrong" but "the rule you are reading is
 * not the rule anyone is being paid by". The remedy is spelled out and left to
 * the admin: there is no button here. Activating the engine re-computes days
 * differently, and a one-click fix would be a silent retroactive payroll change
 * — precisely the class of decision this screen already stops and confirms.
 *
 * `.alert-warning` carries the ink: `text-warning-on-surface` light /
 * `text-warning` dark on a `bg-warning/10` wash. Measured on the page
 * background this renders against: 7.61:1 light, 7.38:1 dark (worst case over
 * every canvas the page uses, 6.86:1 / 6.41:1). The icon inherits
 * `currentColor` on purpose — overriding it to `text-warning` would drop light
 * mode to 4.39:1, below AA for the 14px text it sits beside.
 */
export function WorkdayEngineOffAlert({
  reason,
}: {
  reason: 'not-configured' | 'malformed';
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" className="alert-warning">
      <AlertTriangle size={ICON.feature} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0 space-y-element">
        <p className="font-semibold">{t('settings.workdayEngine.title')}</p>
        <p>
          {t(
            reason === 'malformed'
              ? 'settings.workdayEngine.reason.malformed'
              : 'settings.workdayEngine.reason.notConfigured'
          )}{' '}
          {t('settings.workdayEngine.effect')}
        </p>
        <p>{t('settings.workdayEngine.defaults')}</p>
        <p>{t('settings.workdayEngine.action')}</p>
        <p>{t('settings.workdayEngine.consequence')}</p>
      </div>
    </div>
  );
}

/**
 * The consequence, at the point of change. Shown the moment a field differs
 * from what the server holds: the old value, the new value, and — for a
 * pay-affecting setting — what that does to somebody's wages.
 */
export function ChangeLine({
  before,
  after,
  impact,
  pay,
}: {
  before: string;
  after: string;
  impact?: string;
  pay?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    // `rounded-card` (12px) is the INNER-card role and is correct here: this box
    // is nested two deep — a field row inside `SectionShell`, which is the
    // top-level `rounded-panel`. A nested block one step tighter than the card
    // holding it is what makes the nesting legible.
    //
    // `border-warning-border` rather than `border-warning/40`: the hue has a real
    // border member in the token layer now, so the edge is a designed value
    // rather than an alpha of the solid fill that happened to look right.
    <div
      className={`mt-2 rounded-card border p-2 text-copy-sm ${
        pay
          ? 'border-warning-border bg-warning-surface text-warning-on-surface'
          : 'border-border bg-muted text-foreground'
      }`}
    >
      <p className="flex flex-wrap items-center gap-control">
        {pay && <AlertTriangle size={ICON.inline} aria-hidden="true" className="flex-none" />}
        <span className="sr-only">{t('settings.changeFrom')}</span>
        <span className="line-through">{before}</span>
        <ArrowRight size={ICON.inline} aria-hidden="true" className="shrink-0 rtl:rotate-180" />
        <span className="font-semibold">{after}</span>
        <span className="font-medium">· {t('settings.notSavedYet')}</span>
      </p>
      {impact && <p className="mt-1 leading-relaxed">{impact}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

export interface ControlProps {
  field: SettingField;
  inputId: string;
  value: unknown;
  disabled: boolean;
  onChange: (next: unknown) => void;
  /** Accessible name, used where the control has no visible <label for>. */
  label: string;
}

/** A number box that clamps to the schema bounds on blur. */
function NumberControl({
  field,
  inputId,
  value,
  disabled,
  onChange,
}: ControlProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-element">
      <Input
        id={inputId}
        type="number"
        inputMode="decimal"
        className="w-28"
        value={typeof value === 'number' ? value : ''}
        min={field.min}
        max={field.max}
        step={field.step}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(field.min ?? 0);
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={(e) => {
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          let clamped = n;
          if (field.min !== undefined && clamped < field.min) clamped = field.min;
          if (field.max !== undefined && clamped > field.max) clamped = field.max;
          if (clamped !== n) onChange(clamped);
        }}
      />
      {field.unitKey && (
        <span className="text-copy-sm text-muted-foreground">{t(field.unitKey)}</span>
      )}
    </div>
  );
}

/** Native select styled to match Input. Admin forms don't need a popover. */
function SelectControl({
  field,
  inputId,
  value,
  disabled,
  onChange,
}: ControlProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Select
      id={inputId}
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {(field.options ?? []).map((o) => (
        <option key={o.value} value={o.value}>
          {t(o.labelKey)}
        </option>
      ))}
    </Select>
  );
}

/** Weekend-day picker. `aria-pressed` + a check mark, so not colour alone. */
function WeekdaysControl({
  value,
  disabled,
  onChange,
  label,
}: ControlProps): JSX.Element {
  const { t } = useTranslation();
  const days = Array.isArray(value) ? (value as number[]) : [];
  return (
    <div className="flex flex-wrap gap-control" role="group" aria-label={label}>
      {WEEKDAY_KEYS.map((key, index) => {
        const active = days.includes(index);
        return (
          /* `FilterChip`, size `md` (§6: a `<button aria-pressed>` with an
             on/off palette IS this component). Its palette was one of the two
             answers that component settles — `bg-primary/10` + the SOLID
             `text-primary` here, a solid brand fill three screens over.
             `disabled:transition-none` is the one thing the primitive does not
             say: a disabled control never animates. */
          <FilterChip
            key={key}
            size="md"
            disabled={disabled}
            selected={active}
            className="disabled:transition-none"
            onClick={() =>
              onChange(
                active
                  ? days.filter((d) => d !== index)
                  : [...days, index].sort((a, b) => a - b)
              )
            }
          >
            {/* Second channel beside the fill — §1: never colour alone. */}
            {active ? '✓ ' : ''}
            {t(`settings.weekday.${key}`)}
          </FilterChip>
        );
      })}
    </div>
  );
}

/**
 * "90, 60, 30, 7" → `[90, 60, 30, 7]`. Anything that isn't a non-negative
 * integer is dropped, matching the server schema, and the raw text is kept in
 * local state so a half-typed list isn't reformatted under the cursor.
 */
export function parseDayList(raw: string): number[] {
  const seen = new Set<number>();
  for (const piece of raw.split(/[,\s]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    const n = Number(trimmed);
    if (Number.isInteger(n) && n >= 0) seen.add(n);
  }
  return Array.from(seen).sort((a, b) => b - a);
}

function DayListControl({
  inputId,
  value,
  disabled,
  onChange,
}: ControlProps): JSX.Element {
  const committed = Array.isArray(value) ? (value as number[]).join(', ') : '';
  const [text, setText] = useState(committed);
  // Re-sync when the value changes underneath (a save landing, a discard).
  useEffect(() => setText(committed), [committed]);
  return (
    <Input
      id={inputId}
      value={text}
      disabled={disabled}
      placeholder="90, 60, 30, 7"
      onChange={(e) => {
        setText(e.target.value);
        // Commit on every keystroke: a value typed but never blurred must not
        // be silently dropped when Save is clicked.
        onChange(parseDayList(e.target.value));
      }}
    />
  );
}

function ToggleControl({
  inputId,
  value,
  disabled,
  onChange,
  label,
}: ControlProps): JSX.Element {
  const { t } = useTranslation();
  const checked = value !== false;
  return (
    /*
     * NO `opacity-50` ON THE WRAPPER, and no `justify-end` on it either.
     *
     * The fade was compounding. `Switch` already dims its own track and knob
     * through `peer-disabled:opacity-50`; halving the whole label on top of
     * that drew a disabled switch at 25% — under every readable floor the
     * system has, and worse than the disabled level the token layer declares.
     * The word beside it takes `text-foreground-disabled`, which is that level,
     * stated once (§1: pick a level, never compose one with alpha).
     *
     * The alignment moved up to the row, where the whole card's control rail is
     * decided — see `SettingsFieldRow`. Deciding it here meant a switch hugged
     * the trailing edge while the number box two rows above sat at the leading
     * one, and on a 390px phone, where the row is stacked, the switch flew to
     * the far edge of the screen away from the label naming it.
     */
    <label
      htmlFor={inputId}
      className={`flex min-h-touch items-center gap-element ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <span
        className={`text-copy ${disabled ? 'text-foreground-disabled' : 'text-muted-foreground'}`}
      >
        {t(checked ? 'settings.value.on' : 'settings.value.off')}
      </span>
      <Switch
        id={inputId}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

export function SettingControl(props: ControlProps): JSX.Element {
  const { field, inputId, value, disabled, onChange } = props;
  switch (field.kind) {
    case 'toggle':
      return <ToggleControl {...props} />;
    case 'number':
      return <NumberControl {...props} />;
    case 'select':
      return <SelectControl {...props} />;
    case 'weekdays':
      return <WeekdaysControl {...props} />;
    case 'dayList':
      return <DayListControl {...props} />;
    case 'schedules':
      return (
        <ReportSchedulesEditor
          value={(Array.isArray(value) ? value : []) as ReportSchedule[]}
          onChange={(next) => onChange(next)}
        />
      );
    case 'timezone':
      return (
        <Combobox
          id={inputId}
          value={typeof value === 'string' ? value : ''}
          onChange={(v) => onChange(v)}
          options={timezoneOptions(typeof value === 'string' ? value : '')}
        />
      );
    case 'time':
      return <TimeControl {...props} />;
    case 'date':
      return <DateControl {...props} />;
    case 'mmdd':
      return (
        <Input
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder="MM-DD"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'textarea':
      return (
        <Textarea
          id={inputId}
          rows={7}
          dir={field.forceRtl ? 'rtl' : undefined}
          maxLength={field.maxLength}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          // The one call site that opts back into the drag handle. These are
          // the letter templates — up to 4000 characters of body text, edited
          // on a full settings page rather than in a dialog, so there is no
          // modal edge for the box to escape and seven rows is often not
          // enough to see what you are editing.
          resizable
          // `font-mono` is the field's own meaning, not a re-skin: the letter
          // bodies carry `{{placeholder}}` tokens, and a monospace column is
          // what makes a mistyped brace visible. Size, colour, border and
          // focus all stay the primitive's — including the line-height, which
          // `text-copy-lg` already sets to 1.6 (the old `leading-relaxed` was
          // saying the same thing twice).
          className="font-mono"
        />
      );
    case 'text':
    default:
      return (
        <Input
          id={inputId}
          dir={field.forceRtl ? 'rtl' : undefined}
          maxLength={field.maxLength}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}


/**
 * Shared shell for a settings section so every block looks the same — and the
 * ONE top-level card in this file.
 *
 * THE CARD ROLE. `rounded-panel` (16px), not `rounded-card` (12px). This is the
 * `rounded-card` call site in this file that had to change, and the three that
 * did not are all nested inside one of these: the change line, the schedule row
 * and the pay banner below. `--radius-card` and `--radius-panel` swapped meaning
 * in the warm-paper ramp — 12px is now the INNER card and 16px is what a real
 * card, tile or table shell wants — so a find-and-replace across the four sites
 * would have produced four 12px boxes, three of them right by accident and this
 * one wrong in a way nobody would spot.
 *
 * THE BORDER IS NOT OPTIONAL, and neither is the shadow. The page/card tone step
 * is 1.043:1 in the warm-paper system (§0 of docs/design-system-migration.md):
 * `#FBFAF8` paper against a `#FFFFFF` card is very nearly nothing, so the
 * `#DDD8CE` hairline plus `--shadow-resting` is the whole of what makes this
 * read as a card. A card missing either is a bug, not a style choice.
 *
 * `p-card` (20px) rather than `p-4 sm:p-6` (16/24px). The handoff's card padding
 * band is 18–22px and the room the old 24px bought went to the page gutter
 * instead — a card reads as a card because of the space AROUND it.
 */
export function SectionShell({
  id,
  title,
  blurb,
  pay,
  children,
}: {
  id: string;
  title: string;
  blurb?: string;
  pay?: boolean;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="rounded-panel border border-border bg-surface p-card shadow-resting"
    >
      <div className="flex flex-wrap items-center gap-element">
        {/* `text-h3` — the 17px card-heading step, which also brings Archivo
            with it. `text-base font-semibold` was stock 16px/600, a size with no
            step behind it, so this heading and a card heading elsewhere in the
            app could never be re-tuned together. */}
        <h3 id={`${id}-title`} className="text-h3 text-foreground">
          {title}
        </h3>
        {pay && <PayTag />}
      </div>
      {blurb && (
        <p className="mt-1 max-w-prose text-copy-sm text-muted-foreground">{blurb}</p>
      )}
      {pay && (
        // Nested inside the card above, so `rounded-card` (12px) is right. The
        // icon inherits `currentColor` deliberately: pinning it to `text-warning`
        // drops it to ~4.4:1 in light, below AA for the text it sits beside — the
        // same reasoning the `.alert-warning` note in index.css records.
        <p className={`${noticeInlineClass} mt-3 border-warning-border bg-warning-surface text-copy-sm text-warning-on-surface`}>
          <AlertTriangle size={ICON.inline} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>{t('settings.payBanner')}</span>
        </p>
      )}
      <div className="mt-4">{children}</div>
    </section>
  );
}
